import { TFunction } from "i18next";

import { dateStringToExcelSerial } from "../../../hooks/useEmployeeDataMapper";
import { ValidationResult } from "../EmployeeDataDrawer";

const DATE_PATTERN = /^\d{1,2}[./]\d{1,2}[./]\d{4}$/;

/**
 * Validates a cell value based on the column key
 * @param t - Translation function from useTranslation hook
 * @param colKey - Column key to validate
 * @param value - Value to validate
 */
export const validateCellValue = (
  t: TFunction,
  colKey: string,
  value: string,
): ValidationResult => {
  // Key cannot be changed
  if (colKey === "key") {
    return {
      isValid: false,
      errorMessage: t("paygap.employeeData.validation.keyNotEditable"),
    };
  }

  // Gender validation
  if (colKey === "gender") {
    const lowerValue = value.toLowerCase();

    if (lowerValue !== "male" && lowerValue !== "female") {
      return { isValid: false, errorMessage: "Gender must be: Male or Female" };
    }
  }

  // Age validation: accept age (1-99), birth year (1900-2100), or date (DD/MM/YYYY)
  if (colKey === "age") {
    if (DATE_PATTERN.test(value.trim())) {
      if (dateStringToExcelSerial(value) === null) {
        return {
          isValid: false,
          errorMessage: t("paygap.employeeData.validation.age"),
        };
      }
    } else {
      const numValue = Number(value);

      if (isNaN(numValue) || numValue < 1) {
        return {
          isValid: false,
          errorMessage: t("paygap.employeeData.validation.age"),
        };
      }
    }
  }

  // Years of service validation: accept years (1-99), entry year (1900-2100), or date (DD/MM/YYYY)
  if (colKey === "yearsOfService") {
    if (DATE_PATTERN.test(value.trim())) {
      if (dateStringToExcelSerial(value) === null) {
        return {
          isValid: false,
          errorMessage: t("paygap.employeeData.validation.yearsOfService"),
        };
      }
    } else {
      const numValue = Number(value);

      if (isNaN(numValue) || numValue < 1) {
        return {
          isValid: false,
          errorMessage: t("paygap.employeeData.validation.yearsOfService"),
        };
      }
    }
  }

  // Level of requirements, levelOfRequirements2, levelOfRequirements3, professionalPosition - allow any string
  if (
    colKey === "levelOfRequirements" ||
    colKey === "levelOfRequirements2" ||
    colKey === "levelOfRequirements3" ||
    colKey === "professionalPosition"
  ) {
    // No validation - allow any string value
    return { isValid: true, errorMessage: "" };
  }

  // Employees paid monthly validation (0-100)
  if (colKey === "activityRatePercent") {
    const numValue = Number(value);

    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      return {
        isValid: false,
        errorMessage: t("paygap.employeeData.validation.activityRatePercent"),
      };
    }
  }

  // Employees paid hourly - any number
  if (colKey === "paidHours") {
    const numValue = Number(value);

    if (isNaN(numValue)) {
      return {
        isValid: false,
        errorMessage: t("paygap.employeeData.validation.paidHours"),
      };
    }
  }

  // Basic wage - any number
  if (colKey === "basicWage") {
    const numValue = Number(value);

    if (isNaN(numValue)) {
      return {
        isValid: false,
        errorMessage: t("paygap.employeeData.validation.basicWage"),
      };
    }
  }

  // Allowances - any number
  if (colKey === "allowances") {
    const numValue = Number(value);

    if (isNaN(numValue)) {
      return {
        isValid: false,
        errorMessage: t("paygap.employeeData.validation.allowances"),
      };
    }
  }

  // String fields (allow any string)
  if (
    colKey === "remarks" ||
    colKey === "additionalInformation01" ||
    colKey === "additionalInformation02" ||
    colKey === "additionalInformation03" ||
    colKey === "additionalInformation04" ||
    colKey === "additionalInformation05"
  ) {
    return { isValid: true, errorMessage: "" };
  }

  // Benefit fields validation helper
  const benefitFieldsMap: Record<string, string> = {
    wageCompensationForVacation:
      "paygap.employeeData.validation.wageCompensationForVacation",
    wageCompensationDoctorFamilyLeave:
      "paygap.employeeData.validation.wageCompensationDoctorFamilyLeave",
    sickLeaveIncomeCompensation:
      "paygap.employeeData.validation.sickLeaveIncomeCompensation",
    overtimeWage: "paygap.employeeData.validation.overtimeWage",
    overtimeAllowance: "paygap.employeeData.validation.overtimeAllowance",
    riskAllowance: "paygap.employeeData.validation.riskAllowance",
    standbyDuty: "paygap.employeeData.validation.standbyDuty",
    thirteenthMonthlyWage: "paygap.employeeData.validation.thirteenthMonthlyWage",
    monthlyBonus: "paygap.employeeData.validation.monthlyBonus",
    standardBonus: "paygap.employeeData.validation.standardBonus",
    specialBonus: "paygap.employeeData.validation.specialBonus",
    fiftyFivePlusBonus: "paygap.employeeData.validation.fiftyFivePlusBonus",
    retirementBenefit: "paygap.employeeData.validation.retirementBenefit",
    severancePay: "paygap.employeeData.validation.severancePay",
    specialPayments: "paygap.employeeData.validation.specialPayments",
    supplementaryPensionInsurance:
      "paygap.employeeData.validation.supplementaryPensionInsurance",
    lifeInsurance: "paygap.employeeData.validation.lifeInsurance",
    mealVouchers: "paygap.employeeData.validation.mealVouchers",
    cafeteria: "paygap.employeeData.validation.cafeteria",
    carAllowance: "paygap.employeeData.validation.carAllowance",
    employeeContribution: "paygap.employeeData.validation.employeeContribution",
    usualAnnualWorkingHours:
      "paygap.employeeData.validation.usualAnnualWorkingHours",
  };

  if (colKey in benefitFieldsMap) {
    const numValue = Number(value);

    if (isNaN(numValue)) {
      return {
        isValid: false,
        errorMessage: t(benefitFieldsMap[colKey]),
      };
    }
  }

  // Usual weekly working hours validation (0-168)
  if (colKey === "usualWeeklyWorkingHours") {
    const numValue = Number(value);

    if (isNaN(numValue) || numValue < 0 || numValue > 168) {
      return {
        isValid: false,
        errorMessage: t("paygap.employeeData.validation.usualWeeklyWorkingHours"),
      };
    }
  }

  // Statistical population validation (0-1)
  // if (colKey === "statisticalPopulation") {
  //   const numValue = Number(value);
  //   if (isNaN(numValue) || numValue < 0 || numValue > 1) {
  //     return {
  //       isValid: false,
  //       errorMessage: t("paygap.employeeData.validation.statisticalPopulation"),
  //     };
  //   }
  // }

  return { isValid: true, errorMessage: "" };
};
